import React, { useEffect, useMemo, useState } from 'react'
import { Icon, Label, Loader, Message } from 'semantic-ui-react'
import { StatusPill, MiniSectionHeader } from '../common/DashboardWidgets'
import { AssetCard } from './AssetCard'
import { getTeamAssetDetail } from './assetDashboardService'
import { formatCost, sumMaintenanceCost } from './assetCostUtils'
import { filterAssetsByStatus } from './assetStatusFilters'
import { ASSET_STATUS, STATUS_COLORS } from '../../constants'
import './AssetTeamPanel.css'

// ─── AssetTeamPanel ─────────────────────────────────────────────────

export const AssetTeamPanel = ({ team, assetStatusFilter = 'all', expanded, onToggleExpand }) => {
  const [detail, setDetail] = useState(null)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState(null)

  // ── Summary (from list endpoint — always available) ────────────────────────

  const summary = team.summary ?? {}
  const hasAlerts = (summary.openProblemsCount > 0) || (summary.openStanddownsCount > 0)

  // Fetch detail the first time this panel is expanded (by direct click or Expand All)
  useEffect(() => {
    if (expanded && !detail && !detailLoading) {
      setDetailLoading(true)
      setDetailError(null)
      getTeamAssetDetail(team.id)
        .then(data => {
          setDetail(data)
          setDetailLoading(false)
        })
        .catch(err => {
          setDetailError(err.message || 'Failed to load team asset details')
          setDetailLoading(false)
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded])


  // ── Detail stats (computed from fetched detail) ────────────────────────────

  const assetStats = useMemo(() => {
    const assets = detail?.assets ?? []
    const openProblems = assets.flatMap(a => a.openProblems ?? [])
    return {
      total: assets.length,
      functional:   assets.filter(a => a.status === ASSET_STATUS.FUNCTIONAL).length,
      maintenance:  assets.filter(a => a.status === ASSET_STATUS.MAINTENANCE).length,
      outOfService: assets.filter(a => a.status === ASSET_STATUS.OUT_OF_SERVICE).length,
      noStatus:     assets.filter(a => !a.status).length,
      openProblems: openProblems.length,
      redTags: openProblems.filter(p => p.redTag).length,
      openStanddowns: assets.filter(a => a.openStanddown).length,
      totalMaintenanceCost: assets.reduce((sum, a) => sum + sumMaintenanceCost(a.problemHistory), 0),
    }
  }, [detail])

  // Assets rendered in the card grid respect the dashboard's asset-status filter;
  // the stats bar above always reflects the team's full (unfiltered) breakdown.
  const visibleAssets = useMemo(() => filterAssetsByStatus(detail?.assets, assetStatusFilter), [detail, assetStatusFilter])

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={`tp-panel${hasAlerts ? ' tp-panel--alert' : ''}`}>

      {/* ── Clickable header ──────────────────────────────────────────────── */}
      <div
        role="button"
        data-testid={`asset-team-panel-${team.id}`}
        onClick={() => onToggleExpand(team.id)}
        className={`tp-header${hasAlerts ? ' tp-header--alert' : ''}${expanded ? ' tp-header--expanded' : ''}`}
      >
        <Icon name={expanded ? 'chevron down' : 'chevron right'} style={{ color: '#888', flexShrink: 0 }} />
        <strong className="tp-header__name">{team.name}</strong>
        {team.market && (
          <Label color="blue" size="mini">{team.market.label}</Label>
        )}

        <div className="tp-header__spacer" />

        {/* Quick stats — always visible, from team summary */}
        <div className="tp-header__quickstats">
          <span className="tp-header__stat">
            <Icon name="truck" size="small" style={{ margin: 0 }} /> {summary.assetsTotal ?? 0}
          </span>
          {summary.openProblemsCount > 0 && (
            <Label color="orange" size="mini">{summary.openProblemsCount} open problem{summary.openProblemsCount !== 1 ? 's' : ''}</Label>
          )}
          {summary.openStanddownsCount > 0 && (
            <Label color="red" size="mini">
              <Icon name="tags" style={{ margin: '0 0.25rem 0 0' }} />
              {summary.openStanddownsCount} stand-down{summary.openStanddownsCount !== 1 ? 's' : ''}
            </Label>
          )}
        </div>
      </div>

      {/* ── Expanded content ──────────────────────────────────────────────── */}
      {expanded && (
        <div>
          {detailLoading && (
            <div style={{ padding: '1.5rem', textAlign: 'center' }}>
              <Loader active inline size="small" />
            </div>
          )}
          {detailError && (
            <Message negative size="small" style={{ margin: '0.5rem 1rem' }}>
              <p>{detailError}</p>
            </Message>
          )}
          {detail && <>
            {/* Visual stats breakdown bar */}
            <div className="tp-stats-bar">
              <div className="tp-stats-cell">
                <MiniSectionHeader title="Assets" count={assetStats.total} alert={assetStats.openProblems > 0} />
                <div className="tp-pills-row">
                  {assetStats.functional > 0 && (
                    <StatusPill count={assetStats.functional} label="functional" {...STATUS_COLORS.GREEN} />
                  )}
                  {assetStats.maintenance > 0 && (
                    <StatusPill count={assetStats.maintenance} label="maintenance" {...STATUS_COLORS.YELLOW} />
                  )}
                  {assetStats.outOfService > 0 && (
                    <StatusPill count={assetStats.outOfService} label="out of service" {...STATUS_COLORS.RED} />
                  )}
                  {assetStats.noStatus > 0 && (
                    <StatusPill count={assetStats.noStatus} label="no status" {...STATUS_COLORS.GREY} />
                  )}
                </div>
                {(assetStats.openProblems > 0 || assetStats.openStanddowns > 0) && (
                  <div className="tp-pills-row tp-pills-row--last">
                    {assetStats.openProblems > 0 && (
                      <StatusPill count={assetStats.openProblems} label="open problems" bg={STATUS_COLORS.ORANGE.bg} color={STATUS_COLORS.ORANGE.color} />
                    )}
                    {assetStats.redTags > 0 && (
                      <StatusPill count={assetStats.redTags} label="red tags" bg={STATUS_COLORS.RED.bg} color={STATUS_COLORS.RED.color} />
                    )}
                    {assetStats.openStanddowns > 0 && (
                      <StatusPill count={assetStats.openStanddowns} label="stood down" bg={STATUS_COLORS.RED.bg} color={STATUS_COLORS.RED.color} />
                    )}
                  </div>
                )}
              </div>
              <div className="tp-cost-summary">
                <Icon name="dollar" />
                <span>Total Maintenance Cost</span>
                <strong>{formatCost(assetStats.totalMaintenanceCost)}</strong>
              </div>
            </div>

            {/* Asset cards */}
            {assetStats.total === 0 ? (
              <div className="atp-empty">No assets assigned to this team.</div>
            ) : visibleAssets.length === 0 ? (
              <div className="atp-empty">No assets match the selected status filter.</div>
            ) : (
              <div className="atp-card-grid">
                {visibleAssets.map(asset => <AssetCard key={asset.id} asset={asset} />)}
              </div>
            )}
          </>}
        </div>
      )}
    </div>
  )
}
